<template>
  <div class="pl-4 pr-4 pb-4">
    <v-row class="align-center mb-2 mt-2">
      <v-col><h2 class="ma-0">{{ action === 'update' ? 'Edit category' : 'Add category' }}</h2></v-col>
      <v-col class="text-right"><v-icon color="primary" @click="close">mdi-close</v-icon></v-col>
    </v-row>
    <v-form ref="categoryForm">
      <v-text-field
          v-if="action === 'create'"
          label="ID"
          v-model="newItem.id"
          outlined
          persistent-hint
          hint="Category identifier (displayed in URLs). Use lowercase letters, numbers and hyphens only."
          :rules="[rules.required, rules.slug, rules.uniqueId]"
          class="mb-4"></v-text-field>
      <div v-else class="mb-4">
        <div class="text-caption mb-1">ID</div>
        <v-chip label small>{{ newItem.id }}</v-chip>
      </div>
      <v-text-field
          label="Name"
          v-model="newItem.name"
          outlined
          persistent-hint
          hint="This is the display name of the category. It will be shown to users in admin portal and on the public website."
          :rules="[rules.required]"
          class="mb-4"></v-text-field>
      <v-textarea
          label="Keywords"
          v-model="newItem.keywords"
          outlined
          persistent-hint
          hint="Enter any free-form text, for example a list of keywords or a description of the category. It will be used to help AI automatically detect the category of a job posting."
          rows="4"
          auto-grow
          class="mb-4"></v-textarea>
      <v-text-field
          label="Display priority"
          v-model.number="newItem.displayPriority"
          type="number"
          outlined
          persistent-hint
          hint="Optional. Integer between 0 and 100. Jobs with higher priority categories will be shown higher in public results."
          :rules="[rules.displayPriority]"
          class="mb-4"></v-text-field>
      <v-switch
          label="Public"
          v-model="newItem.public"
          dense
          inset
          class="mt-0 mb-4"></v-switch>
      <v-alert
          v-if="error"
          type="error"
          dismissible
          @input="error = false"
          class="mb-4">
        {{ errorMessage }}
      </v-alert>
      <v-btn color="primary" @click="save" block :loading="isSaving">Save</v-btn>
    </v-form>
  </div>
</template>

<script>
export default {
  name: 'CategoryEditor',
  props: {
    item: {
      type: Object,
      required: true,
    },
    action: {
      type: String,
      required: true,
      default: 'update',
    },
  },
  data() {
    return {
      newItem: {},
      isSaving: false,
      error: false,
      errorMessage: '',
      rules: {
        required: (value) => !!value || 'This field is required.',
        slug: (value) => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) || 'Invalid slug format. Use lowercase letters, numbers and hyphens only.',
        uniqueId: (value) => {
          if (!value) return true;
          const existingCategories = this.$store.state.adminCategories || [];
          const exists = existingCategories.some(cat => cat.id === value);
          return !exists || 'This category ID already exists. Please choose a different one.';
        },
        displayPriority: (value) => {
          if (value === null || value === undefined || value === '') return true;
          const number = Number(value);
          if (!Number.isInteger(number)) return 'Display priority must be an integer.';
          if (number < 0 || number > 100) return 'Display priority must be between 0 and 100.';
          return true;
        },
      },
    };
  },
  watch: {
    item: {
      handler(newItem) {
        if (newItem && Object.keys(newItem).length > 0) {
          this.newItem = JSON.parse(JSON.stringify(newItem));
          // Ensure public field exists, default to true if not set
          if (this.newItem.public === undefined) {
            this.newItem.public = true;
          }
          if (this.newItem.displayPriority === undefined) {
            this.newItem.displayPriority = null;
          }
        } else {
          this.newItem = {
            id: '',
            name: '',
            keywords: '',
            public: true,
            displayPriority: null,
          };
        }
        this.error = false;
        this.errorMessage = '';
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    async save() {
      // Validate form
      if (!this.$refs.categoryForm.validate()) {
        return;
      }

      this.isSaving = true;
      this.error = false;
      this.errorMessage = '';
      
      try {
        const category = { ...this.newItem };
        if (category.displayPriority === '' || category.displayPriority === null || category.displayPriority === undefined) {
          delete category.displayPriority;
        } else {
          category.displayPriority = parseInt(category.displayPriority, 10);
        }

        if (this.action === 'update') {
          await this.$store.dispatch('dispatchUpdateCategory', { category });
        } else {
          await this.$store.dispatch('dispatchPostCategory', { category });
        }
        this.$emit('saved');
        this.$emit('close');
      } catch (error) {
        this.error = true;
        if (error.response) {
          if (error.response.status === 409) {
            this.errorMessage = 'This category ID already exists. Please choose a different ID.';
          } else if (error.response.data && error.response.data.message) {
            this.errorMessage = error.response.data.message;
          } else {
            this.errorMessage = 'Failed to save category. Please try again.';
          }
        } else {
          this.errorMessage = 'Failed to save category. Please try again.';
        }
        console.error('Error saving category:', error);
      } finally {
        this.isSaving = false;
      }
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>

<style>

</style>
